'use client';

import clsx from 'clsx';
// ❗ ПРИМЕЧАНИЕ: Эти импорты оставлены, так как они были в вашем исходном, рабочем коде.
// Если в какой-то части вашего приложения они вызывают ошибку, это связано с настройкой сборки.
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useMemo } from 'react';
// ❗ ПРИМЕЧАНИЕ: Убедитесь, что путь к вашей UI-библиотеке кнопок верный.
import { Button } from '../../../components/ui/button';

// Иконки в виде SVG-компонентов, чтобы избежать проблем с импортами библиотек
const ChevronLeftIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
}

// ✅ ОПТИМИЗАЦИЯ: Компонент обернут в React.memo для предотвращения лишних рендеров
export const Pagination = React.memo(function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  // ИСПРАВЛЕНО: Перенесли вызовы хуков на самый верх.
  // Правила хуков запрещают делать return до вызова хуков.

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // ✅ ОПТИМИЗАЦИЯ: Функция обернута в useCallback, чтобы не создаваться заново при каждом рендере.
  // Логика полностью сохранена: сначала проверяется onPageChange, потом используется router.
  const handlePageChange = useCallback(
    (page: number) => {
      if (page < 1 || page > totalPages) return;

      if (onPageChange) {
        onPageChange(page);
      } else {
        const params = new URLSearchParams(searchParams.toString());
        params.set('page', page.toString());
        router.push(`${pathname}?${params.toString()}`, { scroll: false });
      }
    },
    [onPageChange, totalPages, searchParams, router, pathname],
  );

  // ✅ ОПТИМИЗАЦИЯ И UX: Логика генерации страниц вынесена в useMemo.
  // Теперь отображаются не все страницы, а только самые важные (начало, конец и вокруг текущей),
  // что гораздо удобнее при большом количестве страниц.
  const pages = useMemo(() => {
    const pageNumbers = [];
    const delta = 2; // Сколько страниц показывать вокруг текущей
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let last: number | undefined;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) {
        if (last && i - last === 2) {
          pageNumbers.push(last + 1);
        } else if (last && i - last !== 1) {
          pageNumbers.push('...');
        }
        pageNumbers.push(i);
        last = i;
      }
    }
    return pageNumbers;
  }, [currentPage, totalPages]);

  // ИСПРАВЛЕНО: Проверка перенесена сюда, ПОСЛЕ всех хуков.
  // Если страница всего одна, пагинация не нужна.
  if (totalPages <= 1) {
    return null;
  }

  return (
    // ✅ ДОСТУПНОСТЬ: Весь блок обернут в тег <nav> для семантики
    <nav
      aria-label="Пагинация страниц"
      className="flex flex-col justify-center items-center mt-12 gap-4"
    >
      {/* --- Адаптивность: Кнопка "Показать еще" для мобильных устройств --- */}
      {currentPage < totalPages && (
        <div className="md:hidden">
          <Button
            onClick={() => handlePageChange(currentPage + 1)}
            variant="secondary"
            className="cursor-pointer bg-slate-700 text-slate-200 hover:bg-slate-600"
            aria-label="Показать следующую страницу"
          >
            Показать еще
          </Button>
        </div>
      )}

      {/* --- Адаптивность: Полная пагинация для планшетов и десктопов --- */}
      <div className="hidden md:flex items-center gap-2">
        {/* Кнопка "Назад" */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Перейти на предыдущую страницу"
          className="p-2 rounded-md transition-colors text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>

        {/* Номера страниц */}
        {pages.map((page, index) =>
          typeof page === 'number' ? (
            <button
              key={`${page}-${index}`}
              onClick={() => handlePageChange(page)}
              disabled={page === currentPage}
              aria-label={`Перейти на страницу ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
              className={clsx(
                'w-10 h-10 rounded-md transition-colors text-sm font-medium',
                page === currentPage
                  ? 'bg-sky-600 text-white cursor-default'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700',
              )}
            >
              {page}
            </button>
          ) : (
            <span
              key={`dots-${index}`}
              className="w-10 h-10 flex items-center justify-center text-slate-400"
            >
              ...
            </span>
          ),
        )}

        {/* Кнопка "Вперед" */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Перейти на следующую страницу"
          className="p-2 rounded-md transition-colors text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
    </nav>
  );
});

// Добавляем displayName для удобства отладки в React DevTools
Pagination.displayName = 'Pagination';
